import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import StatusTimeline from "@/components/StatusTimeline";

export default function PortalProject() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [message, setMessage] = useState("");
  const [revision, setRevision] = useState("");
  const [showRevision, setShowRevision] = useState(false);

  const load = () => api.get(`/portal/projects/${id}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  if (user === null) return <div className="pt-40 pb-32 text-center text-slate-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (notFound)
    return (
      <div className="pt-40 pb-32 text-center" data-testid="portal-project-404">
        <h1 className="font-display text-2xl font-bold">Project not found</h1>
        <Link to="/portal" className="mt-4 inline-block text-arsenal hover:underline">Back to portal</Link>
      </div>
    );
  if (!project) return <div className="pt-40 pb-32 text-center text-slate-500" data-testid="portal-project-loading">Loading project…</div>;

  const canReview = ["IN REVIEW", "REVISION REQUESTED"].includes(project.status);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    try {
      await api.post(`/portal/projects/${id}/messages`, { body: message });
      setMessage("");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const requestRevision = async () => {
    try {
      await api.post(`/portal/projects/${id}/revision`, { comment: revision });
      setRevision("");
      setShowRevision(false);
      toast.success("Revision requested");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const approve = async () => {
    try {
      await api.post(`/portal/projects/${id}/approve`);
      toast.success("Approved — nice one.");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const r = await api.get(`/portal/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const downloadAll = async () => {
    try {
      const r = await api.get(`/portal/projects/${id}/download-all`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.title.replace(/ /g, "_")}_deliverables.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="pt-24 sm:pt-28 pb-24" data-testid="portal-project-page">
      <Seo title={project.title} path={`/portal/projects/${id}`} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Link to="/portal" data-testid="project-back-portal" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-slate-400 hover:text-arsenal transition-colors">
          <ArrowLeft className="h-4 w-4" /> PORTAL
        </Link>

        <header className="mt-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="clip-tag bg-arsenal px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] text-white" data-testid="project-status-chip">{project.status}</span>
            <h1 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">{project.title}</h1>
            <p className="mt-2 text-sm text-slate-500">{project.service} — assigned to {project.assigned_artist}</p>
          </div>
          {project.deadline && (
            <div className="border border-white/10 bg-ink-surface px-5 py-3 text-right">
              <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">DEADLINE</p>
              <p className="font-display font-bold text-white">{project.deadline}</p>
            </div>
          )}
        </header>

        <div className="mt-8 border border-white/10 bg-ink-surface p-6 sm:p-8">
          <StatusTimeline status={project.status} history={project.history} />
        </div>

        {project.latest_update && (
          <p className="mt-6 border-l-2 border-arsenal pl-4 text-sm text-slate-300" data-testid="project-latest-update">
            <span className="font-mono text-[10px] tracking-[0.2em] text-arsenal block mb-1">LATEST UPDATE</span>
            {project.latest_update}
          </p>
        )}

        {canReview && (
          <div className="mt-8 border border-arsenal/40 bg-arsenal/5 p-6" data-testid="review-actions">
            <p className="font-mono text-[11px] tracking-[0.25em] text-arsenal">YOUR CALL</p>
            <p className="mt-2 text-sm text-slate-300">Happy with the work? Approve it. Need changes? Request a revision with notes.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={approve} data-testid="approve-btn" className="clip-btn bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-xs px-6 py-3 transition-colors">APPROVE</button>
              <button onClick={() => setShowRevision(!showRevision)} data-testid="revision-toggle" className="clip-btn border border-white/20 hover:border-arsenal text-white font-display font-bold tracking-widest text-xs px-6 py-3 transition-colors">REQUEST REVISION</button>
            </div>
            {showRevision && (
              <div className="mt-4">
                <textarea value={revision} onChange={(e) => setRevision(e.target.value)} rows={3} data-testid="revision-comment" placeholder="What should change? Be as specific as you like." className="aa-input" />
                <button onClick={requestRevision} data-testid="revision-submit" className="clip-btn mt-3 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-xs px-6 py-2.5 transition-colors">SEND REVISION</button>
              </div>
            )}
          </div>
        )}

        <div className="mt-12 grid lg:grid-cols-2 gap-8">
          <section className="border border-white/10 bg-ink-surface p-6 sm:p-8" data-testid="project-messages">
            <h2 className="font-display text-lg font-bold tracking-wide">MESSAGES</h2>
            <div className="mt-5 space-y-4 max-h-96 overflow-y-auto pr-1">
              {project.messages.length === 0 && <p className="text-sm text-slate-600">No messages yet — say hello.</p>}
              {project.messages.map((m) => (
                <div key={m.id} className={`max-w-[85%] ${m.sender === "client" ? "ml-auto" : ""}`} data-testid={`message-${m.id}`}>
                  <p className="font-mono text-[10px] tracking-[0.15em] text-slate-500 mb-1">
                    {m.sender_name} — {new Date(m.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </p>
                  <p className={`px-4 py-3 text-sm leading-relaxed ${m.sender === "client" ? "bg-arsenal/15 border border-arsenal/30 text-white" : "bg-white/5 border border-white/10 text-slate-300"}`}>
                    {m.body}
                  </p>
                </div>
              ))}
            </div>
            <form onSubmit={sendMessage} className="mt-5 flex gap-2">
              <input value={message} onChange={(e) => setMessage(e.target.value)} data-testid="message-input" placeholder="Message the studio…" className="aa-input flex-1" />
              <button type="submit" data-testid="message-send" className="clip-btn bg-arsenal hover:bg-arsenal-bright text-white px-4 transition-colors" aria-label="Send message">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </section>

          <section className="border border-white/10 bg-ink-surface p-6 sm:p-8" data-testid="project-deliverables">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-display text-lg font-bold tracking-wide">DELIVERABLES</h2>
              {project.deliverables.length > 0 && (
                <button onClick={downloadAll} data-testid="download-all-btn" className="clip-tag inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-mono text-[10px] tracking-[0.15em] px-4 py-2 transition-colors">
                  <Download className="h-3.5 w-3.5" /> DOWNLOAD ALL
                </button>
              )}
            </div>
            <div className="mt-5 space-y-3">
              {project.deliverables.length === 0 && <p className="text-sm text-slate-600">Final files land here when the work is approved and delivered.</p>}
              {project.deliverables.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3" data-testid={`deliverable-${f.id}`}>
                  <div>
                    <p className="font-mono text-[11px] tracking-[0.15em] text-arsenal">{f.label}</p>
                    <p className="text-sm text-slate-400">{f.filename}</p>
                  </div>
                  <button onClick={() => downloadFile(f.id, f.filename)} data-testid={`deliverable-download-${f.id}`} className="text-slate-400 hover:text-arsenal transition-colors" aria-label={`Download ${f.filename}`}>
                    <Download className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
